import { styled } from '@mui/material/styles';
import { Dialog, Box, DialogContent, Button, Select } from '@mui/material';

export const StyledDialog = styled(Dialog, {
  shouldForwardProp: (prop) =>
    prop !== 'width' && prop !== 'height' && prop !== 'rightAligned',
})<{ width: string; height: string; rightAligned?: boolean }>(
  ({ width, height, rightAligned }) => ({
    '& .MuiDialog-paper': {
      width: rightAligned ? '940px' : width,
      height: rightAligned ? 'auto' : height,
      maxWidth: 'none',
      overflow: 'hidden',
      borderRadius: rightAligned ? 0 : '16px',
      ...(rightAligned && {
        position: 'fixed',
        right: 0,
        top: 100,
        bottom: 0,
        margin: 0,
        maxHeight: 'none',
        background: 'var(--bg-color)',
        display: 'flex',
        flexDirection: 'column',
      }),
    },
  })
);

export const CloseIcon = styled(Box)({
  position: 'absolute',
  top: 16,
  right: 16,
  fontSize: '24px',
  cursor: 'pointer',
  color: 'var(--instructions)',
  '&:hover': {
    color: 'var(--error)',
  },
});

export const StyledDialogContent = styled(DialogContent, {
  shouldForwardProp: (prop) => prop !== 'rightAligned',
})<{ rightAligned?: boolean }>(({ rightAligned }) => ({
  paddingBottom: 0,
  paddingTop: 2,
  paddingLeft: rightAligned ? 0 : undefined,
  paddingRight: rightAligned ? 0 : undefined,
  overflowY: rightAligned ? 'auto' : 'hidden',
  flex: rightAligned ? 1 : 'none',
  minHeight: 0,
  /* Custom scrollbar styling */
  '&::-webkit-scrollbar': {
    width: '8px',
  },
  '&::-webkit-scrollbar-track': {
    background: '#f1f1f1',
    borderRadius: '4px',
  },
  '&::-webkit-scrollbar-thumb': {
    background: '#c1c1c1',
    borderRadius: '4px',
  },
  '&::-webkit-scrollbar-thumb:hover': {
    background: '#a8a8a8',
  },
}));

export const SubmitButton = styled(Button, {
  shouldForwardProp: (prop) => prop !== 'buttonWidth' && prop !== 'marginleft',
})<{ buttonWidth: string; marginleft?: string }>(
  ({ buttonWidth, marginleft }) => ({
    fontFamily: 'Outfit',
    fontSize: '16px',
    fontWeight: 500,
    width: buttonWidth,
    marginLeft: marginleft || '8px',
    padding: '8px',
    backgroundColor: 'var(--primary-blue)',
    color: 'var(--bg-color)',
    borderRadius: '8px',
    border: '1px solid var(--border-blue)',
    textTransform: 'none',
    '&:hover': {
      backgroundColor: 'var(--hover-blue)',
    },
  })
);

export const CancelButton = styled(Button)({
  width: '100px',
  fontFamily: 'Outfit',
  fontSize: '16px',
  fontWeight: 500,
  marginLeft: '8px',
});

export const FieldLabel = styled(Box)({
  fontSize: '14px',
  fontWeight: 600,
  color: 'var(--subtext)',
  marginBottom: '8px',
  fontFamily: 'Outfit',
});

export const FieldValue = styled(Box)({
  fontSize: '16px',
  fontWeight: 500,
  color: 'var(--primary-navy)',
  marginBottom: '20px',
  fontFamily: 'Outfit',
});

interface StyledSelectProps {
  height?: string | number;
  width?: string | number;
  borderColor?: string;
  fontSize?: string | number;
  hasError?: boolean;
}

export const StyledSelect = styled(Select<string>)<StyledSelectProps>(
  ({
    height = 40,
    width = 200,
    borderColor = 'var(--border)',
    fontSize = 16,
    hasError = false,
  }) => ({
    height,
    width,
    backgroundColor: 'var(--text-bg)',
    color: 'var(--primary-navy)',
    borderRadius: 4,
    overflow: 'hidden',

    '& .MuiSelect-select': {
      padding: '10px 14px',
      fontFamily: 'Outfit',
      fontSize,
    },

    '& .MuiOutlinedInput-notchedOutline': {
      borderColor: hasError ? 'var(--error)' : borderColor,
    },

    '&:hover .MuiOutlinedInput-notchedOutline': {
      borderColor: 'var(--primary-blue)',
    },

    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
      borderColor: 'var(--primary-blue)',
    },

    '& input': {
      fontFamily: 'Outfit',
      fontSize,
    },

    '& input:-webkit-autofill': {
      WebkitBoxShadow: '0 0 0 30px var(--bg-color) inset',
    },
    '& input:-moz-autofill': {
      boxShadow: '0 0 0 30px var(--bg-color) inset',
    },
    '& input:-ms-autofill': {
      boxShadow: '0 0 0 30px var(--bg-color) inset',
    },
  })
);

export const StatusIconBox = styled(Box)({
  position: 'absolute',
  top: '50%',
  right: '12px',
  transform: 'translateY(-50%)',
  fontSize: '16px',
  color: 'var(--instructions)',
  '&:hover': {
    cursor: 'pointer',
  },
});

export const ErrorIconBox = styled(Box)({
  fontSize: 24,
  color: 'var(--error)',
  marginRight: 8,
});

export const TitleWithIconBox = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  flexShrink: 0,
  maxWidth: '100%',
});
